import { ComplianceReportDescription, ReportJobResponse, ExecutiveReportType } from '@herbjoy/apex-api-models';
import { IHttpService } from 'angular';
import { ComplianceReport } from '../../enum/compliance-report';
import { complianceReportOutputFormatList, ComplianceReportOutputFormatSelect, parseComplianceReportOutputFormat } from '../../enum/compliance-report-output-format';
import { JobStatus } from '../../enum/job-status';

export class ReportJobService {
    routePrefix: string;
    constructor(private $http: IHttpService, private apexApiUrl: string) {
        'ngInject';
        this.routePrefix = `${apexApiUrl}/v1/report-jobs`;
    }

    /**
     * Get the compliance report job details with the given ID.
     * @param {string} id The GUID id, as a string.
     */
    retrieveReportJob(id) {
        const descriptions = this.retrieveComplianceReportDescriptions();

        return this.$http({
            method: "GET",
            url: `${this.routePrefix}/${id}`
        }).then(response => descriptions.then(descriptions => mapJobToVM(response.data, descriptions)));
    }

    /**
     * Get compliance report jobs matching the filter criteria
     * @param {any} query Filters for the query
     */
    retrieveReportJobs(query) {
        const descriptions = this.retrieveComplianceReportDescriptions();

        return this.$http<ReportJobResponse[]>({
            method: "GET",
            url: `${this.routePrefix}`,
            params: query
        }).then(response => descriptions.then(descriptions => response.data.map(q => mapJobToVM(q, descriptions))));
    }

    /**
     * Download the file contents of the compliance report generated by the
     * job, if any.
     * @param {string} id The GUID id, as a string
     */
    downloadReportJobDocument(id) {
        return this.$http({
            method: "GET",
            url: `${this.routePrefix}/${id}/document`
        }).then(response => response.data);
    }

    /**
     * Create a new compliance report job with the given details.
     * @param {any} job The compliance report job details
     */
    createReportJob(job) {
        const descriptions = this.retrieveComplianceReportDescriptions();

        return this.$http({
            method: "POST",
            url: `${this.routePrefix}`,
            data: job
        }).then(function (response) {
            return descriptions.then(descriptions => mapJobToVM(response.data, descriptions));
        });
    }

    /**
     * Cancel the compliance report job with the given ID.
     * @param {string} id The GUID id as a string
     */
    cancelReportJob(id) {
        return this.$http<void>({
            method: "DELETE",
            url: `${this.routePrefix}/${id}`
        }).then(response => response.data);
    }

    /**
     * Get a list of report descriptions for compliance reports.
     */
    retrieveComplianceReportDescriptions() {
        return this.$http<ComplianceReportDescription[]>({
            method: "GET",
            url: `${this.routePrefix}/descriptions/compliance`
        }).then(response => response.data);
    }
}

export interface ReportJobVM extends Omit<Omit<ReportJobResponse, "JobStatus">, "ReportType"> {
    ReportType: ComplianceReport;
    OutputFormat: ComplianceReportOutputFormatSelect;
    JobStatus: JobStatus;
}

function mapJobToVM(job: ReportJobResponse, reportDescriptions: ComplianceReportDescription[]): ReportJobVM {
    return {
        ...job,
        ReportType: job.ReportType === ExecutiveReportType.Compliance
            ? new ComplianceReport(job.Reports, reportDescriptions) : null,
        OutputFormat: complianceReportOutputFormatList.find(p =>
            p.Id === parseComplianceReportOutputFormat(job.ReportOutputFormat)),
        JobStatus: JobStatus[job.JobStatus] // job.JobStatus is the stringified enum value.
    }
}
